import type { IFiscalQuotaRepository } from "@/application/ports/repositories/IFiscalQuotaRepository";
import { apiClient, ApiClientError } from "../client";
import { downloadBlob } from "../fileDownload";
import { getLogger } from "@/infrastructure/di/services";

/**
 * Repository for fiscal-quota-scoped templated document actions.
 *
 * Mirrors the contract endpoints used by ``InvestmentRepository`` for
 * financing / communication previews and downloads, but anchored on a
 * ``fiscal_quota`` (the audit document is per-quota by domain rules).
 */
export class FiscalQuotaRepository implements IFiscalQuotaRepository {
  private readonly logger = getLogger();
  private readonly basePath = "/fiscal-quotas";

  async previewExpenseAudit(
    quotaUuid: string,
    request: { template_uuid: string },
  ): Promise<string> {
    try {
      const blob = await apiClient.postBlob(
        `${this.basePath}/${quotaUuid}/actions/preview-expense-audit`,
        request,
        { timeout: 30000 },
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      throw this.handleError(error, `preview expense audit for fiscal quota ${quotaUuid}`);
    }
  }

  async downloadExpenseAudit(quotaUuid: string, request: { template_uuid: string }): Promise<void> {
    try {
      const { blob, filename } = await apiClient.postBlobWithFilename(
        `${this.basePath}/${quotaUuid}/actions/generate-expense-audit`,
        request,
        `expense_audit_${quotaUuid}.pdf`,
        { timeout: 60000 },
      );
      downloadBlob(blob, filename);
    } catch (error) {
      throw this.handleError(error, `download expense audit for fiscal quota ${quotaUuid}`);
    }
  }

  private handleError(error: unknown, operation: string): Error {
    this.logger.error(`FiscalQuotaRepository.${operation} failed:`, error);
    if (error instanceof ApiClientError) return error;
    return error instanceof Error ? error : new Error(`${operation} failed: ${String(error)}`);
  }
}
